import React, { useEffect, useRef, useState } from 'react';
import { Player } from '@lordicon/react';
import ScrollReveal from 'scrollreveal';
import { useTranslation } from '../LanguageContext';
import styles from './ThirdScreen.module.css';
import home from "../icons/wired-outline-1609-bungalow-hover-pinch.json"
import magazine from "../icons/wired-outline-1752-residence-mansion-in-reveal.json"
import space from "../icons/wired-outline-516-square-footage-hover-layout-change.json"
import parraleximage from "../images/פנטהאוז תל אביבי - הגוש הגדול 2.webp"

const ThirdScreen = () => {
  const { t, isEnglish } = useTranslation();
  const sectionRef = useRef(null);
  const elementsRef = useRef([]);
  const playerRefs = useRef([]);
  const principlesRef = useRef(null);
  const cardRefs = useRef([]);
  const quoteRef = useRef(null);
  const parallaxRef = useRef(null);
  const painCardsRef = useRef([]);
  const [revealedCards, setRevealedCards] = useState([false, false, false]);
  const [activeProgress, setActiveProgress] = useState(0);

  useEffect(() => {
    const elements = elementsRef.current.filter(Boolean);
    
    elements.forEach((el, index) => {
      ScrollReveal().reveal(el, {
        origin: 'bottom',
        distance: '50px',
        duration: 1000,
        delay: 100 * index,
        easing: 'cubic-bezier(0.5, 0, 0, 1)',
        reset: false,
      });
    });

    if (quoteRef.current) {
      ScrollReveal().reveal(quoteRef.current, {
        origin: 'bottom',
        distance: '80px',
        duration: 1400,
        delay: 200,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        opacity: 0,
        scale: 0.9,
        reset: false,
      });
    }

    cardRefs.current.forEach((card, index) => {
      if (card) {
        ScrollReveal().reveal(card, {
          origin: 'bottom',
          distance: '100px',
          duration: 1200,
          delay: 150 * index,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          opacity: 0,
          scale: 0.85,
          rotate: { x: 0, y: 0, z: 5 },
          reset: false,
        });
      }
    });

    return () => ScrollReveal().destroy();
  }, []);

  // Intersection Observer לאיתור כשהקלפים נחשפים והפעלת האנימציות
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardIndex = painCardsRef.current.indexOf(entry.target);
            if (cardIndex !== -1 && !revealedCards[cardIndex]) {
              // הפעלת האנימציה ברגע החשיפה
              setTimeout(() => {
                playerRefs.current[cardIndex]?.playFromBeginning();
              }, 300); // דיליי קטן אחרי החשיפה
              
              setRevealedCards(prev => {
                const newRevealed = [...prev];
                newRevealed[cardIndex] = true;
                return newRevealed;
              });
            }
          }
        });
      },
      {
        threshold: 0.3, // הקלף צריך להיות חשוף ב־30% כדי להפעיל
        rootMargin: '-50px'
      }
    );

    painCardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [revealedCards]);

  // Parallax effect for image - IMPROVED
  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      
      const scrolled = window.pageYOffset;
      const rect = parallaxRef.current.getBoundingClientRect();
      const elementTop = rect.top + scrolled;
      const elementHeight = rect.height;
      const windowHeight = window.innerHeight;
      
      if (scrolled + windowHeight > elementTop && scrolled < elementTop + elementHeight) {
        const progress = (scrolled + windowHeight - elementTop) / (windowHeight + elementHeight);
        // הגדלת האפקט פי 2 - מ־150 ל־300
        const translateY = progress * 300 - 150;
        const scale = 1 + (progress * 0.15); // הוספת זום עדין
        parallaxRef.current.style.transform = `translateY(${translateY}px) scale(${scale})`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleComplete = (index) => {
    setTimeout(() => {
      playerRefs.current[index]?.playFromBeginning();
    }, 3000);
  };

  const icons = [home, space, magazine];
  const painPoints = t.third.painPoints.map((text, i) => ({
    text,
    icon: icons[i]
  }));

  const principles = t.third.principles;

  return (
    <section className={styles.thirdScreen} ref={sectionRef}>
      {/* Background Elements */}
      <div className={styles.bgGradient}></div>
      <div className={styles.bgPattern}></div>
      <div className={styles.floatingOrb1}></div>
      <div className={styles.floatingOrb2}></div>
      
      <div className={styles.container}>
        
        {/* ===== SECTION 1: Opening Hook ===== */}
        <div className={styles.openingSection} ref={(el) => (elementsRef.current[0] = el)}>
          <h2 className={styles.openingTitle}>{t.third.openingTitle}</h2>
          <p className={styles.openingText}>
            {t.third.openingText}
          </p>
        </div>

        {/* ===== SECTION 2: Pain Points - DARK GLASSMORPHISM ===== */}
        <div className={styles.painSection} ref={(el) => (elementsRef.current[1] = el)}>
          <h3 className={styles.painTitle}>{t.third.painTitle}</h3>
          <div className={styles.painGrid}>
            {painPoints.map((point, index) => (
              <div 
                key={index} 
                className={styles.painCard}
                ref={(el) => (painCardsRef.current[index] = el)}
              >
                <div className={styles.painCardContent}>
                  <div className={styles.painIconWrapper}>
                    <Player 
                      icon={point.icon} 
                      ref={(el) => (playerRefs.current[index] = el)}
                      size="100%" 
                      onComplete={() => handleComplete(index)}
                      className={styles.painIcon}
                    />
                  </div>
                  <p className={styles.painText}>{point.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== SECTION 3: Mission Statement ===== */}
        <div className={styles.missionSection} ref={(el) => (elementsRef.current[2] = el)}>
          <h3 className={styles.missionTitle}>{t.third.missionTitle}</h3>
          <p className={styles.missionText} dangerouslySetInnerHTML={{ __html: t.third.missionText1 }} />
          <p className={styles.missionText} dangerouslySetInnerHTML={{ __html: t.third.missionText2 }} />
        </div>

        {/* ===== PARALLAX IMAGE SECTION ===== */}
        <div className={styles.parallaxSection}>
          <div className={styles.parallaxContainer}>
            <div className={styles.parallaxImageWrapper} ref={parallaxRef}>
              <img 
                src={parraleximage} 
                alt="Interior Design" 
                className={styles.parallaxImage}
              loading="lazy" decoding="async" />
              <div className={styles.parallaxOverlay}></div>
            </div>
          </div>
        </div>

        {/* ===== SECTION 4: Core Belief (Quote) - WITH SCROLL REVEAL ===== */}
   

        {/* ===== SECTION 5: Principles - IMPROVED SCROLL REVEAL ===== */}
        <div className={styles.principlesSection} ref={principlesRef}>
          <h3 className={styles.principlesTitle}>{t.third.principlesTitle}</h3>
          
          <div className={styles.principlesGrid}>
            {principles.map((principle, index) => (
              <div 
                key={index} 
                className={styles.principleCard}
                ref={(el) => (cardRefs.current[index] = el)}
              >
                <div className={styles.principleNumberCircle}>
                  <span className={styles.principleNumberText}>{principle.number}</span>
                </div>
                <h4 className={styles.principleTitle}>{principle.title}</h4>
                <p className={styles.principleText}>{principle.text}</p>
              </div>
            ))}
          </div>
        </div>

{/*        
        <div className={styles.finalSection} ref={(el) => (elementsRef.current[5] = el)}>
          <h3 className={styles.finalTitle}>חשוב לי שתזכרו:</h3>
          <p className={styles.finalText}>
            אני לא "עוד אחוז מהשיפוץ" שלכם. אני האחוז שמבטיח שקניית הבית וההשקעה שלכם יהיו ההחלטה הכי טובה שקיבלתם.
          </p>
          <p className={styles.finalHighlight}>
            אני כאן כדי שהבית שלכם יהיה כזה שלא תרצו לצאת ממנו - גם לא לחופשה בבית מלון.
          </p>
        </div> */}

        {/* ===== SECTION 7: Transition ===== */}
        <div className={styles.transitionSection} ref={(el) => (elementsRef.current[6] = el)}>
          <p className={styles.transitionText}>
            {t.third.transitionText}
          </p>
          <p className={styles.transitionSubtext}>
            {t.third.transitionSubtext}
          </p>
          <div className={styles.scrollArrow}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ThirdScreen;